import { useEffect } from "react";
import { Link, useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Grid } from "@/comn/components";
import { comnUtils, comnEnvs } from "@/comn/utils"; // 프로젝트 공통 유틸
import { Page, Group, Layout, Button, FormControl } from "@/comn/components"; // 화면 구성 컴포넌트
import { useForm, useFetch, useResource, useGrid, useModal, useStore, useToast, usePopup, usePage } from "@/comn/hooks"; // hook
import { BASE, URLS, APIS, SF_CSTM_INFO } from "@/tra/tancis/ptli/usr/cstm/services/PtliCstmMgmtService";

/*
 * Edit customs information
 * !== 세관 정보 수정 ==!
 */
export const PTLI0901003S = (): JSX.Element => {
    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "COM0016" },
            { area: "comnCd", comnCd: "CLR0064" },
            { area: "comnCd", comnCd: "COM0019" },
        ],
    });

    const { pgeUid } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM"],
    });
    const { t } = useTranslation();
    const navigate = useNavigate();
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast();
    const { cstmOfceCd } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!
    const { getParams } = usePopup();
    const params = getParams(); /* * */
    const [searchParams, setSearchParams] = useSearchParams();
    const rqstPgeTp = searchParams.get("rqstPgeTp");

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Customs Search !== 세관 등록 ==!
        cstmInfo: useForm({
            defaultSchema: SF_CSTM_INFO,
            defaultValues: {},
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        getCstmInfo: useFetch({
            api: (data) => APIS.getCstmInfo(cstmOfceCd),
            enabled: !!cstmOfceCd,
            onSuccess: (data) => {
                const cstmInfo = data.cstmInfo.content;
                const ptliCstmLangList = cstmInfo.ptliCstmLangList;

                if (ptliCstmLangList) {
                    ptliCstmLangList.map((item: any) => {
                        switch (item.langCd) {
                            case "EN":
                                cstmInfo.cstmEnNm = item.cstmNm;
                                break;
                            case "TZ":
                                cstmInfo.cstmTzNm = item.cstmNm;
                                break;
                            default:
                                cstmInfo.cstmKoNm = item.cstmNm;
                                break;
                        }
                    });
                }
                form.cstmInfo.setValues(cstmInfo);
            },
            onError: (error) => {},
            showToast: true,
        }),
        // Save Repacking Item Application !== 세관 등록 ==!
        saveCstm: useFetch({
            api: (data) => APIS.saveCstm({ ...data }),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.com.00062",
                    onConfirm: () => {
                        // 나의 할일에서 넘어온 경우
                        if (params !== undefined && params.todo === "Y") {
                            navigate(`${URLS.ptli0901004q}/${cstmOfceCd}`);
                            // modal.postMessage("success");
                        } else {
                            navigate(`${URLS.ptli0901004q}/${cstmOfceCd}`);
                        }
                    },
                    onCancel: () => {
                        navigate(`${URLS.ptli0901004q}/${cstmOfceCd}`);
                    },
                });
            },
            onError: (error) => {
                console.log(error);
                if (error.response.status === 400) {
                    console.log(error.response.data);
                    form.cstmInfo.setErrors(error.response.data.errors);
                }

                modal.openModal({
                    content: "msg.00006",
                });
            },
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // customs registration !== 세관 등록 ==!
        saveCstm: form.cstmInfo.handleSubmit(
            (data) => {
                modal.openModal({
                    content: "msg.00101", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                    onConfirm: () => {
                        fetch.saveCstm.fetch(data);
                    },
                });
            },
            () => {
                toast.showToast({ type: "warning", content: "msg.00002" });
            },
        ),
    };

    return (
        <Page>
            <form>
                <Group>
                    <Group.Body>
                        <Group.Section>
                            <Group.Row>
                                <Group.Control {...form.cstmInfo.schema.cstmOfceCd} readOnly={true}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.cstmInfo.schema.cstmTzNm}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.cstmInfo.schema.cstmEnNm}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.cstmInfo.schema.cstmKoNm}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.cstmInfo.schema.cstmAddr}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.cstmInfo.schema.cstmTelno}></Group.Control>
                                <Group.Control {...form.cstmInfo.schema.cstmFaxNo}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.cstmInfo.schema.scnrYn}></Group.Control>
                                <Group.Control {...form.cstmInfo.schema.dclrYn}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.cstmInfo.schema.cstmTpCd}></Group.Control>
                                <Group.Control {...form.cstmInfo.schema.regnTpCd}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.cstmInfo.schema.regnCd}></Group.Control>
                                <Group.Control {...form.cstmInfo.schema.cstmTlxNo}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.cstmInfo.schema.cstmDesc}></Group.Control>
                            </Group.Row>
                        </Group.Section>
                    </Group.Body>
                </Group>
            </form>
            <Layout direction="row">
                <Layout.Left>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            navigate(`${URLS.ptli0901004q}/${cstmOfceCd}`);
                        }}
                    >
                        {t("B_PRE")}
                    </Button>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.ptli0901001q);
                        }}
                    ></Button>
                </Layout.Left>
                <Layout.Right>
                    <Button
                        role="save"
                        onClick={() => {
                            handler.saveCstm();
                        }}
                    ></Button>
                </Layout.Right>
            </Layout>
        </Page>
    );
};
